import { Container, Typography, Paper, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/theme';

export default function TermsPage() {
  const { t } = useTranslation();
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#FFFFFF' }}>{t('pages.terms.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Last updated: March 2026</Typography>

        <Section title="1. Acceptance of Terms">
          By accessing or using CoffeeMates ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.
        </Section>

        <Section title="2. Description of Service">
          CoffeeMates is a social networking platform that facilitates structured, topic-based gatherings at partner coffee shops in Baku, Azerbaijan. The Platform connects users with events, manages pre-payment vouchers, and coordinates group meetups.
        </Section>

        <Section title="3. User Accounts">
          You must provide a valid phone number or email address to create an account. You are responsible for maintaining the confidentiality of your account. You must be at least 18 years old to use the Platform. You agree to provide accurate, current, and complete information during registration.
        </Section>

        <Section title="4. Voucher Purchases & Payments">
          All voucher purchases are pre-payments for beverage services at partner coffee shops. Voucher prices include a service fee (1 AZN) retained by CoffeeMates. Refunds are issued automatically when events are cancelled or do not meet minimum capacity. Refunds for voluntary cancellations are credited to your in-app wallet. All payments are processed through authorized payment gateways. CoffeeMates does not store credit card or bank information.
        </Section>

        <Section title="5. Event Participation">
          Events require a minimum number of participants to proceed. If the minimum is not met, you will be offered an alternative event (Pivot) or a full refund. Events are time-limited (typically 90 minutes). Attendance is confirmed via QR code scanning at the partner venue.
        </Section>

        <Section title="6. Community Standards">
          CoffeeMates operates a zero-tolerance policy for harassment, discrimination, intoxication, and abusive behavior. Political topics are prohibited from event curation. Users who violate community standards may be permanently banned. All participants must acknowledge the CoffeeMates Safety Promise before purchasing vouchers.
        </Section>

        <Section title="7. Partner Coffee Shops">
          Partner venues are independently owned and operated. CoffeeMates is not responsible for the quality of food, beverages, or service at partner venues. Partner venues agree to maintain CoffeeMates hosting standards as outlined in the Partner Agreement.
        </Section>

        <Section title="8. Master Classes">
          Master Class events are created by premium users or partner venues and require admin approval. Master Class fees are charged in addition to the standard voucher price. The Master Class fee is paid to the event creator. CoffeeMates retains the standard service fee from the voucher component.
        </Section>

        <Section title="9. Intellectual Property">
          The CoffeeMates name, logo, and platform design are the property of CoffeeMates. User-generated content (ratings, reviews) may be used by CoffeeMates for quality improvement and marketing purposes.
        </Section>

        <Section title="10. Limitation of Liability">
          CoffeeMates is a platform facilitator and is not liable for interactions between users at events. We are not liable for any injuries, damages, or losses arising from event participation. Our total liability is limited to the amount you paid for the specific voucher in question.
        </Section>

        <Section title="11. Termination">
          CoffeeMates may suspend or terminate your account at any time for violation of these Terms. You may delete your account at any time by contacting support.
        </Section>

        <Section title="12. Changes to Terms">
          We may update these Terms from time to time. We will notify users of material changes via email or in-app notification. Continued use of the Platform after changes constitutes acceptance.
        </Section>

        <Section title="13. Governing Law">
          These Terms are governed by the laws of the Republic of Azerbaijan. Any disputes shall be resolved in the courts of Baku, Azerbaijan.
        </Section>

        <Section title="14. Contact">
          For questions about these Terms, contact us at support@coffeemates.biz
        </Section>
      </Paper>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Typography variant="body1" sx={{ color: colors.neutral[700], lineHeight: 1.7 }}>{children}</Typography>
    </Box>
  );
}
